import axios from 'axios'
import config from '@/utils/config'

export interface UploadedFile {
  url: string
  name: string
}

export interface UploadFilesResult {
  uploadFile: string
  uploadfileName: string
}

// 结构最小化，兼容 element-plus 的 UploadFile 与 UploadUserFile
export interface UploadableFile {
  name: string
  url?: string
  raw?: File
}

export const UPLOAD_MAX_SIZE_MB = 5
export const UPLOAD_ALLOWED_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'bmp',
  'webp',
  'pdf',
  'doc',
  'docx',
  'docm',
  'dotx',
]
export const UPLOAD_ACCEPT = UPLOAD_ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(',')
export const UPLOAD_FORMAT_LABEL = UPLOAD_ALLOWED_EXTENSIONS.join('、')

export const UPLOAD_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'bmp', 'webp']
export const UPLOAD_IMAGE_ACCEPT = UPLOAD_IMAGE_EXTENSIONS.map((ext) => `.${ext}`).join(',')
export const UPLOAD_IMAGE_FORMAT_LABEL = UPLOAD_IMAGE_EXTENSIONS.join('、')

function getFileExtension(name: string): string {
  const idx = name.lastIndexOf('.')
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : ''
}

export function validateUploadFile(file: UploadableFile): string | null {
  const raw = file.raw
  if (!raw) return null

  const ext = getFileExtension(raw.name || file.name)
  if (!UPLOAD_ALLOWED_EXTENSIONS.includes(ext)) {
    return `不支持的文件类型，仅支持图片和文档（${UPLOAD_FORMAT_LABEL}）`
  }
  if (raw.size > UPLOAD_MAX_SIZE_MB * 1024 * 1024) {
    return `文件大小不能超过 ${UPLOAD_MAX_SIZE_MB}MB`
  }
  return null
}

function resolveUploadedFileData(responseData: any) {
  return responseData?.data?.data ?? responseData?.data ?? responseData ?? {}
}

export async function uploadSingleFile(file: UploadableFile): Promise<UploadedFile> {
  if (file.url) {
    return { url: file.url, name: file.name }
  }
  if (!file.raw) {
    throw new Error(`文件读取失败: ${file.name}`)
  }

  const invalidMessage = validateUploadFile(file)
  if (invalidMessage) {
    throw new Error(invalidMessage)
  }

  const fileBytes = await fileToBase64(file.raw)
  const payload = {
    fileName: file.name,
    fileBytes,
  }

  const BaseUrl = config.BASE_URL || ''
  const res = await axios.post(`${BaseUrl}/tgsys/uploadFile`, payload, {
    withCredentials: true,
    headers: { 'Content-Type': 'application/json' },
  })

  const uploaded = resolveUploadedFileData(res.data)
  const url = uploaded.url || uploaded.fileUrl || uploaded.path || ''
  const name = uploaded.fileName || uploaded.name || file.name

  if (!url) {
    throw new Error(`上传失败: ${file.name}`)
  }

  file.url = url
  return { url, name }
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      resolve(result.split(',')[1] || result)
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

export async function uploadFiles(fileList: UploadableFile[]): Promise<UploadFilesResult> {
  const urls: string[] = []
  const names: string[] = []

  for (const file of fileList) {
    const { url, name } = await uploadSingleFile(file)
    urls.push(url)
    names.push(name)
  }

  return {
    uploadFile: urls.join(','),
    uploadfileName: names.join(','),
  }
}

export function parseFileList(
  uploadFile?: string | null,
  uploadfileName?: string | null,
): UploadedFile[] {
  const urls = (uploadFile || '').split(',').map((s) => s.trim()).filter(Boolean)
  const names = (uploadfileName || '').split(',').map((s) => s.trim()).filter(Boolean)
  return urls.map((url, i) => ({ url, name: names[i] || url }))
}
